import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import UpgradeButton from '@/components/UpgradeButton';
import FloatingHelpButton from '@/components/FloatingHelpButton';
import MetricoolConnectionSection from '@/components/MetricoolConnectionSection';
import { getUser } from '@/utils/auth';
import { ArrowRight, CreditCard, Clock, CheckCircle, Warning, Crown, VideoCamera, Sparkle, ArrowsClockwise, Bug, Lightning, LockKey, Eye, EyeSlash } from '@phosphor-icons/react';
import { createPortalSession } from '@/utils/stripe';
import { billingAPI, videoAPI, authAPI } from '@/utils/api';
import api from '@/utils/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import BusinessCreditsSettingsSection from '@/components/BusinessCreditsSettingsSection';
import { brandBrainAPI } from '@/utils/api';
import { BrandBrainReviewCard } from '@/pages/BrandBrainSetupPage';
import { useNavigate } from 'react-router-dom';

// Brand Brain Section — persistent visual identity (Round 113).
// Renders the same review card used during onboarding; every save
// PATCHes /api/brand-brain so downstream builders pick up changes
// automatically on their next request.
const BrandBrainSection = () => {
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await brandBrainAPI.get();
        if (!cancelled) setBrand(d?.brand_brain || null);
      } catch (err) {
        console.warn('brand-brain get failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleField = (path, value) => {
    setBrand((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      const keys = path.split('.');
      let cursor = next;
      for (let i = 0; i < keys.length - 1; i += 1) {
        cursor[keys[i]] = { ...(cursor[keys[i]] || {}) };
        cursor = cursor[keys[i]];
      }
      cursor[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    if (!brand) return;
    setSaving(true);
    try {
      const fields = {
        business_name: brand.business_name,
        tagline: brand.tagline,
        primary_colors: brand.primary_colors,
        secondary_colors: brand.secondary_colors,
        fonts: brand.fonts,
        brand_voice: brand.brand_voice,
        brand_personality: brand.brand_personality,
        brand_style: brand.brand_style,
        icon_style: brand.icon_style,
        illustration_style: brand.illustration_style,
        photography_style: brand.photography_style,
        website_url: brand.website_url,
      };
      const r = await brandBrainAPI.patch(fields);
      setBrand(r?.brand_brain || brand);
      toast.success('Brand Brain updated.');
    } catch (err) {
      console.error(err);
      toast.error('Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bg-white border border-slate-200 rounded-xl shadow-sm p-8"
      data-testid="settings-brand-brain-section"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Brand Brain</h2>
          <p className="text-sm text-slate-500 mt-1">
            Your visual identity — used automatically by every builder in PayGBot.
          </p>
        </div>
        <button
          onClick={() => navigate('/brand-brain-setup')}
          data-testid="settings-brand-brain-rerun"
          className="text-sm text-emerald-700 hover:text-emerald-900 underline"
        >
          Re-run setup
        </button>
      </div>

      {loading && <div className="text-sm text-slate-500">Loading Brand Brain…</div>}

      {!loading && brand && (
        <div className="text-slate-900">
          <BrandBrainReviewCard
            brand={brand}
            onField={handleField}
            onBack={null}
            onSave={save}
            busy={saving}
          />
        </div>
      )}
    </div>
  );
};


// Change Password Section Component
const ChangePasswordSection = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (formData.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    try {
      await authAPI.changePassword(
        formData.currentPassword,
        formData.newPassword,
        formData.confirmPassword
      );
      toast.success('Password changed successfully!');
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setIsExpanded(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <LockKey size={24} className="text-slate-600" />
          <h2 className="text-xl font-semibold text-slate-900">Change Password</h2>
        </div>
        {!isExpanded && (
          <button
            onClick={() => setIsExpanded(true)}
            data-testid="expand-change-password-btn"
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-lg transition-colors"
          >
            Change Password
          </button>
        )}
      </div>
      
      {isExpanded && (
        <form onSubmit={handleSubmit} className="space-y-4 mt-6 pt-6 border-t border-slate-200">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Current Password</label>
            <div className="relative">
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                value={formData.currentPassword}
                onChange={(e) => handleChange('currentPassword', e.target.value)}
                required
                data-testid="current-password-input"
                className="w-full px-4 py-3 pr-12 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                placeholder="Enter current password"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showCurrentPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">New Password</label>
            <div className="relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={(e) => handleChange('newPassword', e.target.value)}
                required
                minLength={8}
                data-testid="new-password-input"
                className="w-full px-4 py-3 pr-12 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                placeholder="Enter new password (min 8 characters)"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showNewPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Confirm New Password</label>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => handleChange('confirmPassword', e.target.value)}
              required
              data-testid="confirm-password-input"
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              placeholder="Confirm new password"
            />
            {formData.newPassword && formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
              <p className="text-red-500 text-sm mt-1">Passwords do not match</p>
            )}
          </div>
          
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setIsExpanded(false);
                setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
              }}
              className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.currentPassword || !formData.newPassword || formData.newPassword !== formData.confirmPassword}
              data-testid="save-password-btn"
              className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : 'Save New Password'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

const MEMORY_FIELDS = [
  { key: 'business_name', label: 'Business Name', placeholder: 'e.g., Happy Paws Pet Sitting', type: 'text' },
  { key: 'niche', label: 'Niche', placeholder: 'e.g., Dog sitting and pet care', type: 'text' },
  { key: 'target_audience', label: 'Target Audience', placeholder: 'e.g., Busy pet owners in Austin TX', type: 'textarea' },
  { key: 'core_problem', label: 'Core Problem', placeholder: 'What does your audience struggle with?', type: 'textarea' },
  { key: 'core_solution', label: 'Core Solution', placeholder: 'How do you solve that problem?', type: 'textarea' },
  { key: 'primary_offer', label: 'Primary Offer', placeholder: 'e.g., Monthly Deep Clean Package', type: 'text' },
  { key: 'price_point', label: 'Price Point', placeholder: 'e.g., $199/visit', type: 'text' },
  { key: 'business_type', label: 'Business Type', placeholder: 'e.g., local_service, digital_product, coaching', type: 'text' },
  { key: 'selected_platform', label: 'Platform', placeholder: 'e.g., Instagram, TikTok, YouTube', type: 'text' },
  { key: 'content_focus', label: 'Content Focus', placeholder: 'What topics do you post about?', type: 'text' },
  { key: 'tone_of_voice', label: 'Tone of Voice', placeholder: 'e.g., warm, professional, edgy, playful', type: 'text' },
  { key: 'monetization_goal', label: 'Monetization Goal', placeholder: 'e.g., $5,000/month from recurring clients', type: 'text' },
];

const SOURCE_LABELS = {
  manual_edit: { label: 'Manual edit', color: 'bg-blue-100 text-blue-700' },
  business_builder: { label: 'Business Builder', color: 'bg-purple-100 text-purple-700' },
  offer_builder: { label: 'Offer Builder', color: 'bg-amber-100 text-amber-700' },
  onboarding: { label: 'Onboarding', color: 'bg-emerald-100 text-emerald-700' },
};

const FIELD_KEY_TO_LABEL = MEMORY_FIELDS.reduce((acc, f) => {
  acc[f.key] = f.label;
  return acc;
}, {});

const formatRelativeTime = (isoStr) => {
  if (!isoStr) return '';
  const then = new Date(isoStr).getTime();
  const diff = Date.now() - then;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(isoStr).toLocaleDateString();
};

const MyBusinessSection = () => {
  const [memory, setMemory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [isExpanded, setIsExpanded] = useState(false);
  const [changelog, setChangelog] = useState([]);
  const [showChangelog, setShowChangelog] = useState(false);

  useEffect(() => {
    loadMemory();
    loadChangelog();
  }, []);

  const loadMemory = async () => {
    try {
      const response = await api.get('/business-memory/profile');
      if (response.data.has_memory && response.data.memory) {
        setMemory(response.data.memory);
        setFormData(response.data.memory);
      }
    } catch {
      // Silent
    } finally {
      setLoading(false);
    }
  };

  const loadChangelog = async () => {
    try {
      const response = await api.get('/business-memory/changelog');
      setChangelog(response.data.entries || []);
    } catch {
      // Silent
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.post('/business-memory/profile', formData);
      if (response.data.success) {
        setMemory(response.data.memory);
        toast.success('Business memory updated! All future generations will use these details.');
        setIsExpanded(false);
        loadChangelog();
      }
    } catch (error) {
      toast.error('Failed to save business memory');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const filledCount = MEMORY_FIELDS.filter(f => formData[f.key]).length;

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8">
        <div className="animate-pulse h-6 bg-slate-200 rounded w-48 mb-4" />
        <div className="animate-pulse h-4 bg-slate-100 rounded w-64" />
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8" data-testid="my-business-section">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xl font-semibold text-slate-900">My Business</h2>
        <span className="text-sm text-slate-500">{filledCount}/{MEMORY_FIELDS.length} fields</span>
      </div>
      <p className="text-sm text-slate-600 mb-4">
        This is your business identity. Every tool in PayGBot uses this to generate niche-specific content for your business.
      </p>

      {!memory && !isExpanded && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
          <p className="text-amber-800 text-sm font-medium">Complete your business setup to unlock full personalization.</p>
        </div>
      )}

      <button
        onClick={() => setIsExpanded(!isExpanded)}
        data-testid="edit-business-memory-btn"
        className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center space-x-1 mb-4"
      >
        <span>{isExpanded ? 'Collapse' : (memory ? 'Edit Business Details' : 'Set Up My Business')}</span>
        <ArrowRight size={14} className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
      </button>

      {isExpanded && (
        <div className="space-y-4 pt-2">
          {MEMORY_FIELDS.map(field => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-slate-700 mb-1">{field.label}</label>
              {field.type === 'textarea' ? (
                <textarea
                  value={formData[field.key] || ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  data-testid={`memory-${field.key}`}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm resize-none"
                />
              ) : (
                <input
                  type="text"
                  value={formData[field.key] || ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  data-testid={`memory-${field.key}`}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                />
              )}
            </div>
          ))}
          <div className="flex space-x-3 pt-4">
            <button
              onClick={() => setIsExpanded(false)}
              className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-business-memory-btn"
              className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save My Business'}
            </button>
          </div>
        </div>
      )}

      {!isExpanded && memory && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          {MEMORY_FIELDS.filter(f => memory[f.key]).slice(0, 6).map(f => (
            <div key={f.key} className="bg-slate-50 rounded-lg px-3 py-2">
              <span className="text-slate-500 text-xs">{f.label}</span>
              <p className="text-slate-800 truncate">{memory[f.key]}</p>
            </div>
          ))}
        </div>
      )}

      {/* Memory Change Log */}
      {memory && changelog.length > 0 && (
        <div className="mt-6 pt-6 border-t border-slate-200">
          <button
            onClick={() => setShowChangelog(!showChangelog)}
            data-testid="toggle-memory-changelog-btn"
            className="flex items-center space-x-2 text-sm font-medium text-slate-700 hover:text-slate-900"
          >
            <Clock size={16} />
            <span>Memory Change Log ({changelog.length})</span>
            <ArrowRight size={14} className={`transition-transform ${showChangelog ? 'rotate-90' : ''}`} />
          </button>

          {showChangelog && (
            <div className="mt-4 space-y-2 max-h-80 overflow-y-auto" data-testid="memory-changelog-list">
              {changelog.map(entry => {
                const src = SOURCE_LABELS[entry.source] || { label: entry.source, color: 'bg-slate-100 text-slate-700' };
                const fieldLabel = FIELD_KEY_TO_LABEL[entry.field] || entry.field;
                return (
                  <div key={entry.id} className="bg-slate-50 border border-slate-100 rounded-lg p-3 text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-slate-900">{fieldLabel}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${src.color}`}>
                          {src.label}
                        </span>
                      </div>
                      <span className="text-xs text-slate-500">{formatRelativeTime(entry.timestamp)}</span>
                    </div>
                    <div className="text-xs text-slate-600">
                      {entry.old_value ? (
                        <>
                          <span className="text-red-600 line-through mr-1">{entry.old_value.slice(0, 80)}</span>
                          <span className="text-emerald-700">→ {entry.new_value.slice(0, 80)}</span>
                        </>
                      ) : (
                        <span className="text-emerald-700">Set to: {entry.new_value.slice(0, 120)}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PlanBadge = ({ plan, status, trialDaysRemaining, stripeBackedTrial }) => {
  const colors = {
    trial: 'bg-amber-100 text-amber-700',
    free: 'bg-amber-100 text-amber-700',
    starter: 'bg-blue-100 text-blue-700',
    pro: 'bg-purple-100 text-purple-700',
    elite: 'bg-amber-100 text-amber-700',
    growth: 'bg-purple-100 text-purple-700',
  };

  const statusColors = {
    active: 'bg-emerald-100 text-emerald-700',
    trialing: 'bg-blue-100 text-blue-700',
    past_due: 'bg-red-100 text-red-700',
    canceled: 'bg-slate-100 text-slate-700',
  };

  const displayPlan = plan === 'free' ? 'trial' : plan;

  return (
    <div className="flex items-center space-x-2">
      <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${colors[displayPlan] || colors.trial}`}>
        {displayPlan || 'Trial'}
      </span>
      {/* Show trial days remaining for trialing status */}
      {status === 'trialing' && trialDaysRemaining !== undefined && trialDaysRemaining > 0 && (
        <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700">
          {trialDaysRemaining} days left
        </span>
      )}
      {/* Show Stripe-backed indicator */}
      {stripeBackedTrial && status === 'trialing' && (
        <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
          Stripe Trial
        </span>
      )}
      {/* Show status for non-trialing paid plans */}
      {status && status !== 'trialing' && plan !== 'trial' && plan !== 'free' && (
        <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${statusColors[status] || statusColors.active}`}>
          {status === 'active' ? 'Active' : status.replace('_', ' ')}
        </span>
      )}
    </div>
  );
};

const SettingsPage = () => {
  const user = getUser();
  const [searchParams] = useSearchParams();
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [billingInfo, setBillingInfo] = useState(null);
  const [videoUsage, setVideoUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgradeLoading, setUpgradeLoading] = useState(null);
  const [testModeStatus, setTestModeStatus] = useState(null);
  const [testModeLoading, setTestModeLoading] = useState(false);
  
  // PART 3 & 4: Video credits state
  const [videoCredits, setVideoCredits] = useState(0);

  useEffect(() => {
    // Show success message when returning from billing portal
    if (searchParams.get('portal') === 'success') {
      toast.success('Billing settings updated successfully!');
      window.history.replaceState({}, '', '/settings');
    }
    if (searchParams.get('success') === 'true') {
      toast.success('Subscription activated successfully!');
      window.history.replaceState({}, '', '/settings');
    }
  }, [searchParams]);

  useEffect(() => {
    fetchBillingData();
    fetchTestModeStatus();
    fetchVideoCredits();
  }, []);
  
  // PART 3: Fetch video credit balance
  const fetchVideoCredits = async () => {
    try {
      const response = await videoAPI.getCredits();
      setVideoCredits(response.data.video_credits || 0);
    } catch (error) {
      console.error('Failed to fetch video credits:', error);
    }
  };

  const fetchTestModeStatus = async () => {
    try {
      const response = await api.get('/auth/test-mode/status');
      setTestModeStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch test mode status:', error);
    }
  };

  const handleActivateTestMode = async (plan) => {
    setTestModeLoading(true);
    try {
      const response = await api.post(`/auth/test-mode/activate?plan=${plan}`);
      toast.success(response.data.message);
      setTestModeStatus({ test_mode_active: true, current_plan: plan });
      // Reload page to update user data
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate test mode');
    } finally {
      setTestModeLoading(false);
    }
  };

  const handleDeactivateTestMode = async () => {
    setTestModeLoading(true);
    try {
      const response = await api.post('/auth/test-mode/deactivate');
      toast.success(response.data.message);
      setTestModeStatus({ test_mode_active: false, current_plan: 'trial' });
      // Reload page to update user data
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate test mode');
    } finally {
      setTestModeLoading(false);
    }
  };

  const fetchBillingData = async () => {
    setLoading(true);
    try {
      const [billingRes, videoRes] = await Promise.all([
        billingAPI.getInfo(),
        videoAPI.getUsage()
      ]);
      setBillingInfo(billingRes.data);
      setVideoUsage(videoRes.data);
    } catch (error) {
      console.error('Failed to fetch billing info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setLoadingPortal(true);
    try {
      await createPortalSession();
    } catch (error) {
      setLoadingPortal(false);
    }
  };

  const handleUpgrade = async (plan) => {
    setUpgradeLoading(plan);
    try {
      const response = await billingAPI.createCheckout(plan);
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setUpgradeLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // CURRENT PRICING - March 2026
  // All plans include 7-day Stripe-backed trial
  // Phase G (2026-02-06) — V2 collapses to a single subscription. Every legacy
  // signup path (starter/growth/pro) now points to the same Business Strategist
  // price ID in .env so upstream code that reads `plans[0]` still works. Kept as
  // a single-item list rather than deleted so the surrounding JSX doesn't need
  // to be refactored.
  const plans = [
    {
      id: 'starter',
      name: 'Business Strategist',
      price: 49,
      trial: true,
      trialDays: 7,
      features: [
        '30 monthly Business Credits',
        'Business Brain + Brand Brain + Business Kit',
        'Live Market + Opportunity Intelligence',
        'Every builder unlocked — no template downgrade',
        'Business Credit packs available any time',
      ],
      description: 'The complete AI Business Strategist',
      popular: true,
    },
  ];

  // Developer mode - only show if explicitly enabled via query param
  const isDeveloperMode = searchParams.get('dev') === 'true';

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" data-testid="settings-title">
                Your Settings
              </h1>
              <p className="text-lg text-slate-600">Manage your account and billing here</p>
            </div>
            {/* Upgrade Button for Free/Trial Users */}
            {billingInfo && (billingInfo.plan_tier === 'free' || billingInfo.plan_tier === 'trial') && (
              <UpgradeButton 
                onClick={() => handleUpgrade('pro')}
                variant="compact"
                currentPlan={billingInfo.plan_tier}
              />
            )}
          </div>
        </div>

        <div className="space-y-6">
          {/* Account Info */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-6">Your Account</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                <div className="text-lg text-slate-900" data-testid="user-name-display">{user?.name}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                <div className="text-lg text-slate-900" data-testid="user-email-display">{user?.email}</div>
              </div>
            </div>
          </div>

          {/* Change Password Section */}
          <ChangePasswordSection />

          {/* My Business — Persistent Business Memory Editor */}
          <MyBusinessSection />

          {/* Brand Brain — persistent visual identity (Round 113) */}
          <BrandBrainSection />

          {/* Round 218A Phase 8D-2 — Business Credits detail section.
              Total / monthly / purchased / allowance / next refill /
              rollover policy. Never shows per-asset credit costs. */}
          <BusinessCreditsSettingsSection />

          {/* Metricool Integration Section */}
          <MetricoolConnectionSection />

          {/* Billing & Subscription */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-slate-900">Your Plan</h2>
              {!loading && billingInfo && (
                <PlanBadge 
                  plan={billingInfo.plan_tier} 
                  status={billingInfo.subscription_status} 
                  trialDaysRemaining={billingInfo.trial_days_remaining}
                  stripeBackedTrial={billingInfo.stripe_backed_trial}
                />
              )}
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <ArrowsClockwise size={24} className="animate-spin text-slate-400" />
              </div>
            ) : billingInfo ? (
              <div className="space-y-6">
                {/* Current Plan Info */}
                <div className="p-6 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl border border-slate-200">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center space-x-2 mb-2">
                        <Crown size={24} className="text-amber-500" weight="fill" />
                        <span className="text-2xl font-bold text-slate-900" data-testid="plan-name">
                          {(() => {
                            const t = billingInfo.plan_tier;
                            if (!t) return 'Business Strategist';
                            if (t === 'trial' || t === 'free' || t === 'pending') return 'Business Strategist Trial';
                            return 'Business Strategist';
                          })()}
                        </span>
                      </div>
                      {/* Round 218A Phase 8A — unified strategist copy.
                          Removed per-tier price strings ("$29/starter",
                          "$79/pro", "$199/elite"). One plan, one price. */}
                      <p className="text-slate-600">
                        {(billingInfo.plan_tier === 'trial' || billingInfo.plan_tier === 'free' || billingInfo.plan_tier === 'pending')
                          ? 'Your Strategist is ready — activate to unlock finished production'
                          : '$49/month · Unlimited strategy · Includes monthly Business Credits'}
                      </p>
                    </div>
                    
                    {billingInfo.subscription_status === 'trialing' && billingInfo.trial_end && (
                      <div className="text-right">
                        <div className="flex items-center space-x-1 text-blue-600">
                          <Clock size={16} />
                          <span className="text-sm font-medium">
                            {billingInfo.stripe_backed_trial ? 'Stripe Trial' : 'Trial'} Active
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">
                          {billingInfo.trial_days_remaining} days remaining
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Ends {formatDate(billingInfo.trial_end)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Subscription Status */}
                {billingInfo.subscription_status && billingInfo.plan_tier !== 'free' && (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-white border border-slate-200 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        {billingInfo.is_active ? (
                          <CheckCircle size={20} className="text-emerald-500" weight="fill" />
                        ) : (
                          <Warning size={20} className="text-red-500" weight="fill" />
                        )}
                        <span className="font-medium text-slate-900">Status</span>
                      </div>
                      <p className={`text-sm ${billingInfo.is_active ? 'text-emerald-600' : 'text-red-600'}`}>
                        {billingInfo.subscription_status === 'trialing' && 'Trial Period'}
                        {billingInfo.subscription_status === 'active' && 'Active'}
                        {billingInfo.subscription_status === 'past_due' && 'Payment Past Due'}
                        {billingInfo.subscription_status === 'canceled' && 'Canceled'}
                      </p>
                    </div>

                    {billingInfo.trial_end && billingInfo.subscription_status === 'trialing' && (
                      <div className="p-4 bg-white border border-slate-200 rounded-lg">
                        <div className="flex items-center space-x-2 mb-2">
                          <Clock size={20} className="text-blue-500" />
                          <span className="font-medium text-slate-900">Trial Ends</span>
                        </div>
                        <p className="text-sm text-slate-600">
                          {formatDate(billingInfo.trial_end)}
                        </p>
                        {billingInfo.stripe_backed_trial && (
                          <p className="text-xs text-green-600 mt-1">
                            Managed by Stripe • Billing starts automatically
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Content & Video Prompts - Simplified unlimited display */}
                {videoUsage && (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <VideoCamera size={20} className="text-emerald-600" weight="fill" />
                        <span className="font-medium text-emerald-900">Content & Video Prompts</span>
                      </div>
                      <span className="text-emerald-700 font-medium">
                        {videoUsage.unlimited ? 'Unlimited' : 'Trial Access'}
                      </span>
                    </div>
                    <p className="text-sm text-emerald-600 mt-2">
                      Create unlimited content and video prompts
                    </p>
                  </div>
                )}

                {/* Round 218A Phase 8A — Removed the "Daily AI
                    Generations" meter. Strategy is unlimited under
                    the unified Strategist model. In Phase 8B this
                    slot will be replaced by the Business Credits
                    balance widget (production metering). */}

                {/* Actions */}
                {billingInfo.has_stripe_customer && (
                  <div className="pt-4 border-t border-slate-200">
                    <button
                      onClick={handleManageBilling}
                      disabled={loadingPortal}
                      data-testid="manage-billing-button"
                      className="flex items-center space-x-2 px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CreditCard size={20} />
                      <span>{loadingPortal ? 'Loading...' : 'Manage Billing'}</span>
                      {!loadingPortal && <ArrowRight size={18} weight="bold" />}
                    </button>
                    <p className="text-xs text-slate-600 mt-2">
                      Update payment method, view invoices, and manage your subscription
                    </p>
                  </div>
                )}

                {/* Upgrade Options */}
                {(billingInfo.plan_tier === 'free' || billingInfo.plan_tier === 'trial') && (
                  <div className="pt-6 border-t border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">Upgrade Your Plan</h3>
                    <p className="text-sm text-slate-600 mb-4">
                      All plans include a 7-day free trial. Billing starts automatically after trial ends.
                    </p>
                    <div className="grid md:grid-cols-3 gap-4">
                      {plans.map((plan) => (
                        <motion.div
                          key={plan.id}
                          whileHover={{ scale: 1.02 }}
                          className={`relative p-5 rounded-xl border-2 transition-all ${
                            plan.popular 
                              ? 'border-purple-500 bg-purple-50' 
                              : 'border-slate-200 bg-white hover:border-slate-300'
                          }`}
                        >
                          {plan.popular && (
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                              <span className="px-3 py-1 bg-purple-600 text-white text-xs font-medium rounded-full">
                                Most Popular
                              </span>
                            </div>
                          )}
                          <div className="text-center">
                            <h4 className="text-lg font-bold text-slate-900">{plan.name}</h4>
                            <div className="mt-2">
                              <span className="text-3xl font-bold text-slate-900">${plan.price}</span>
                              <span className="text-slate-600">/mo</span>
                            </div>
                            {plan.trial ? (
                              <p className="text-sm text-purple-600 mt-1">{plan.trialDays}-day free trial</p>
                            ) : (
                              <p className="text-sm text-slate-500 mt-1">Billed immediately</p>
                            )}
                            <p className="text-xs text-slate-400 mt-0.5">Then ${plan.price}/mo</p>
                            <ul className="mt-4 space-y-2 text-left">
                              {plan.features.slice(0, 3).map((feature, idx) => (
                                <li key={idx} className="flex items-start space-x-2 text-sm text-slate-600">
                                  <CheckCircle size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" weight="fill" />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                            <button
                              onClick={() => handleUpgrade(plan.id)}
                              disabled={upgradeLoading === plan.id}
                              className={`w-full mt-4 py-2.5 font-medium rounded-lg transition-colors ${
                                plan.popular
                                  ? 'bg-purple-600 hover:bg-purple-700 text-white'
                                  : 'bg-slate-900 hover:bg-slate-800 text-white'
                              } disabled:opacity-50`}
                            >
                              {upgradeLoading === plan.id ? 'Loading...' : (plan.trial ? 'Start Free Trial' : 'Start Now')}
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Round 218A Phase 8A — Removed per-tier upgrade
                    blocks ("Upgrade from Starter", "Upgrade from Pro
                    to Elite"). Under the unified Strategist model
                    every paying customer receives the same
                    strategist. Tier upsells no longer exist. */}
              </div>
            ) : (
              <p className="text-slate-600">Unable to load billing information</p>
            )}
          </div>

          {/* Preferences */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Preferences</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-slate-900">Email Notifications</div>
                  <div className="text-sm text-slate-600">Receive updates about your content and videos</div>
                </div>
                <input type="checkbox" className="w-5 h-5 text-blue-600" defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-slate-900">Save Results Automatically</div>
                  <div className="text-sm text-slate-600">Auto-save all generated content</div>
                </div>
                <input type="checkbox" className="w-5 h-5 text-blue-600" defaultChecked />
              </div>
            </div>
          </div>

          {/* Developer Test Mode - Hidden in normal mode, show only with ?dev=true */}
          {isDeveloperMode && (
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-xl shadow-sm p-8">
            <div className="flex items-center space-x-3 mb-4">
              <Bug size={24} className="text-purple-600" weight="fill" />
              <h2 className="text-xl font-semibold text-slate-900">Developer Test Mode</h2>
            </div>
            <p className="text-slate-600 text-sm mb-6">
              Test the full PayGBot experience with a simulated paid plan. Perfect for testing the complete user flow from Income Path to Today's Content Ready.
            </p>
            
            {testModeStatus?.test_mode_active ? (
              <div className="space-y-4">
                <div className="bg-green-100 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 text-green-700">
                    <Lightning size={20} weight="fill" />
                    <span className="font-medium">Test Mode Active</span>
                  </div>
                  <p className="text-sm text-green-600 mt-1">
                    You're currently testing with the <span className="font-semibold uppercase">{testModeStatus.current_plan}</span> plan.
                  </p>
                </div>
                <button
                  onClick={handleDeactivateTestMode}
                  disabled={testModeLoading}
                  data-testid="deactivate-test-mode-btn"
                  className="w-full py-3 bg-slate-600 hover:bg-slate-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {testModeLoading ? 'Deactivating...' : 'Deactivate Test Mode'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-slate-500">Select a plan to test:</p>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => handleActivateTestMode('starter')}
                    disabled={testModeLoading}
                    data-testid="activate-starter-btn"
                    className="py-3 px-4 bg-blue-100 hover:bg-blue-200 text-blue-700 font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    Starter
                  </button>
                  <button
                    onClick={() => handleActivateTestMode('growth')}
                    disabled={testModeLoading}
                    data-testid="activate-growth-btn"
                    className="py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    Growth
                  </button>
                  <button
                    onClick={() => handleActivateTestMode('pro')}
                    disabled={testModeLoading}
                    data-testid="activate-pro-btn"
                    className="py-3 px-4 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    Pro
                  </button>
                </div>
              </div>
            )}
            
            {/* Restart Wizard Option */}
            <div className="mt-6 pt-6 border-t border-purple-200">
              <h3 className="text-sm font-medium text-slate-700 mb-2">Onboarding Wizard</h3>
              <p className="text-sm text-slate-500 mb-3">
                Restart the setup wizard to reconfigure your business profile and generate a new content plan.
              </p>
              <button
                onClick={async () => {
                  try {
                    await api.post('/auth/wizard/restart');
                    localStorage.removeItem('wizard_skipped');
                    toast.success('Wizard reset! Redirecting to dashboard...');
                    window.location.href = '/dashboard';
                  } catch (error) {
                    toast.error('Failed to restart wizard');
                  }
                }}
                data-testid="restart-wizard-btn"
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-lg transition-colors text-sm"
              >
                Restart Setup Wizard
              </button>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Floating Help Button */}
      <FloatingHelpButton />
    </div>
  );
};

export default SettingsPage;
